import * as THREE from "three";
import type { AttachKind } from "@rietz/shared";
import { makePreviewLights } from "../../render/previewLights.js";
import {
  buildPart,
  buildWeapon,
  type BuiltPart,
  type BuiltWeapon,
} from "../../render/weaponBuilder.js";

/**
 * An interactive model of a gun being built, or of a single part. `setWeapon`
 * is the Werkbank's render with callout lines pinned to `modAnchors`;
 * `setPart` is the Inspect dialog's picture of a loose attachment — same drag,
 * lights and builder, only the FRAMING differs (`frameOnPart`).
 *
 * A second tiny `WebGLRenderer` on its own canvas, since the world renderer is
 * the render loop's. No animation loop: it renders on mount, on change and
 * during a drag; the model is rebuilt only when the subject KEY changes, so
 * the pane may call on every re-render. `dispose()` gives the GL context back
 * — browsers cap contexts around 16 and kill the OLDEST, which could be the
 * game's own canvas.
 *
 * IT OWNS ITS CANVAS: `forceContextLoss()` poisons the element for good, and
 * StrictMode mounts every effect twice, so a canvas from the component's JSX
 * would be permanently blank in dev. A refused GL context leaves an empty
 * square; the rest of the Werkbank keeps working.
 */

/**
 * Where the camera sits, metres: a side profile, as in an inventory inspection
 * window.
 */
const CAM_POS = new THREE.Vector3(1.55, 0.18, 0.02);

/** The gun spans roughly z ∈ [−0.7, +0.36]; this centres it on the pivot. */
const MODEL_Z_OFFSET = 0.17;

/**
 * Look target with a WEAPON on the turntable: a little below the bore, so the
 * receiver sits on the centre line.
 */
const WEAPON_LOOK_Y = -0.045;

/**
 * How much of the viewport's narrower half-angle a PART's bounding SPHERE fills
 * — the worst case in every direction, so the margin holds however the player
 * drags it.
 */
const PART_FILL = 0.85;

export class ModPreview {
  private readonly canvas: HTMLCanvasElement;
  private readonly renderer: THREE.WebGLRenderer | null;
  private readonly scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(30, 1, 0.05, 12);
  /** Everything rotates as one, so the model itself is never touched. */
  private readonly turntable = new THREE.Group();
  private readonly lights: THREE.Object3D[] = [];

  /**
   * Whatever is on the turntable. A `BuiltPart` has no muzzle, grips or
   * `modAnchors`, which is why `publishAnchors` narrows first.
   */
  private model: BuiltWeapon | BuiltPart | null = null;
  /** Identity of the model currently built: `defId|mod,ids` or `part:defId`. */
  private builtKey = "";

  private disposed = false;
  private displayScale = 1;

  private readonly widthPx: number;
  private readonly heightPx: number;
  private readonly onAnchors?: (points: ModAnchorPoints) => void;

  /** `host` is an empty box; the canvas and DOM callouts share its pixels. */
  constructor(
    host: HTMLElement,
    widthPx: number,
    heightPx: number = widthPx,
    onAnchors?: (points: ModAnchorPoints) => void,
  ) {
    this.widthPx = widthPx;
    this.heightPx = heightPx;
    this.onAnchors = onAnchors;
    const canvas = document.createElement("canvas");
    canvas.width = widthPx;
    canvas.height = heightPx;
    canvas.style.width = `${widthPx}px`;
    canvas.style.height = `${heightPx}px`;
    canvas.style.display = "block";
    host.appendChild(canvas);
    this.canvas = canvas;

    let renderer: THREE.WebGLRenderer | null = null;
    try {
      renderer = new THREE.WebGLRenderer({ canvas, antialias: true, alpha: true });
      renderer.setPixelRatio(this.pixelRatio());
      renderer.setSize(widthPx, heightPx, false);
    } catch {
      // No GL context (headless, a browser out of contexts, a blocked canvas).
      renderer = null;
    }
    this.renderer = renderer;

    this.scene.name = "modPreview";
    this.scene.add(this.turntable);
    this.camera.aspect = widthPx / heightPx;
    this.frameOnWeapon();

    // The same three-light rig the world and the viewmodel use, so a scope
    // looks
    // the same here as in the hands.
    for (const l of makePreviewLights()) {
      this.scene.add(l);
      this.lights.push(l);
    }
  }

  /**
   * Shows `defId` built with `mods`. A no-op when neither changed, so the pane
   * may call it on every render.
   */
  setWeapon(defId: string, mods: readonly string[]): void {
    if (this.disposed) return;
    const key = `${defId}|${mods.join(",")}`;
    if (key === this.builtKey) return;
    this.builtKey = key;

    this.clearModel();
    // `"missing"` view: the pane hands us the real fitted parts, so an empty
    // slot
    // is genuinely empty — the turntable must not lie about what was taken off.
    const built = buildWeapon(defId, mods, { bare: "missing" });
    built.group.position.z = MODEL_Z_OFFSET;
    this.turntable.add(built.group);
    this.model = built;
    // Every gun is about a metre around the origin, so one camera frames all;
    // re-asserting it undoes a part's framing.
    this.frameOnWeapon();
    this.renderOnce();
  }

  /**
   * Shows the LOOSE PART `defId` on its own. No-op when already the subject. An
   * id with no model clears the turntable rather than throwing.
   */
  setPart(defId: string): void {
    if (this.disposed) return;
    const key = `part:${defId}`;
    if (key === this.builtKey) return;
    this.builtKey = key;

    this.clearModel();
    const built = buildPart(defId);
    if (built !== null) {
      // FRAMED BEFORE IT IS PARENTED, so the bounding box is measured in the
      // part's own space.
      this.frameOnPart(built.group);
      this.turntable.add(built.group);
      this.model = built;
    }
    this.renderOnce();
  }

  /** Keep the backing buffer sharp when a host CSS-scales the viewport. The
   *  fullscreen workbench blows the 640x280 box up well past 1.5x; `pixelRatio`
   *  still caps the product, so the ceiling here is only about how far a host
   *  may stretch. */
  setDisplayScale(scale: number): void {
    if (this.disposed || !Number.isFinite(scale)) return;
    const next = THREE.MathUtils.clamp(scale, 0.5, 3);
    if (Math.abs(next - this.displayScale) < 0.001) return;
    this.displayScale = next;
    this.renderer?.setPixelRatio(this.pixelRatio());
    this.renderer?.setSize(this.widthPx, this.heightPx, false);
    this.renderOnce();
  }

  /** Rotate whatever is on the turntable in response to a pointer drag — the
   *  complete modular assembly, or the single part being inspected. */
  rotateBy(deltaX: number, deltaY: number): void {
    if (this.disposed || this.model === null) return;
    this.turntable.rotation.y += deltaX * 0.009;
    this.turntable.rotation.x = THREE.MathUtils.clamp(
      this.turntable.rotation.x + deltaY * 0.007,
      -Math.PI * 0.42,
      Math.PI * 0.42,
    );
    this.renderOnce();
  }

  start(): void {
    this.renderOnce();
  }

  stop(): void {
    // Static by design. Kept as part of the lifecycle API used by the pane.
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stop();

    this.clearModel();
    for (const l of this.lights) this.scene.remove(l);
    this.lights.length = 0;
    this.scene.clear();
    // Hands the GL context back. The canvas goes with it — a
    // `forceContextLoss()`ed
    // element is useless afterwards.
    this.renderer?.dispose();
    this.renderer?.forceContextLoss();
    this.canvas.remove();
  }

  private renderOnce(): void {
    if (this.disposed || this.renderer === null) return;
    this.renderer.render(this.scene, this.camera);
    this.publishAnchors();
  }

  /**
   * Takes the model off the turntable and gives its materials back. Both
   * setters and `dispose` go through here.
   */
  private clearModel(): void {
    const built = this.model;
    if (built === null) return;
    this.turntable.remove(built.group);
    built.dispose();
    this.model = null;
  }

  /** The authored side profile every gun is framed by. See `CAM_POS`. */
  private frameOnWeapon(): void {
    this.camera.near = 0.05;
    this.camera.far = 12;
    this.camera.position.copy(CAM_POS);
    this.camera.lookAt(0, WEAPON_LOOK_Y, 0);
    this.camera.updateProjectionMatrix();
  }

  /**
   * Centres a PART on the pivot and moves the camera in until it fills the
   * frame. A part is authored in its HOST's space (a magazine 19 cm below a
   * receiver that is not here), so its bounding box is shifted onto the pivot;
   * and it is SMALL, so the camera comes in along the same direction (moving
   * it, not scaling the turntable) to where the bounding SPHERE subtends
   * `PART_FILL` of the narrower half-angle. Near and far planes are re-derived
   * from that distance — the authored 0.05 m near plane sits in front of the
   * smallest parts.
   */
  private frameOnPart(group: THREE.Group): void {
    // The group is not on the turntable yet, so `matrixWorld` IS its local
    // matrix and the box comes out in the space `position` is expressed in.
    group.updateMatrixWorld(true);
    const box = new THREE.Box3().setFromObject(group);
    if (box.isEmpty()) return;
    group.position.sub(box.getCenter(new THREE.Vector3()));

    const sphere = box.getBoundingSphere(new THREE.Sphere());
    // A floor, because a part whose whole model is one flat plate has a radius
    // of a few millimetres and would otherwise pull the camera onto it.
    const radius = Math.max(sphere.radius, 0.01);
    const halfFovY = THREE.MathUtils.degToRad(this.camera.fov) / 2;
    const halfFovX = Math.atan(Math.tan(halfFovY) * this.camera.aspect);
    const half = Math.min(halfFovY, halfFovX) * PART_FILL;
    const distance = radius / Math.sin(half);

    this.camera.position.copy(CAM_POS).setLength(distance);
    this.camera.near = Math.max(0.001, distance - radius * 2);
    this.camera.far = distance + radius * 4;
    this.camera.lookAt(0, 0, 0);
    this.camera.updateProjectionMatrix();
  }

  private pixelRatio(): number {
    const deviceScale = Math.max(1, globalThis.devicePixelRatio ?? 1);
    // Keep native display density when shrinking, and allocate enough backing
    // pixels for the largest supported enlargement. The cap bounds GPU memory.
    return Math.min(4, deviceScale * Math.max(1, this.displayScale));
  }

  /**
   * Where the Werkbank's callout lines point, in canvas pixels. ONLY FOR A
   * WEAPON: a loose part has no `modAnchors`, so a part publishes nothing.
   */
  private publishAnchors(): void {
    const model = this.model;
    if (this.onAnchors === undefined || model === null || !("modAnchors" in model)) return;
    const points: ModAnchorPoints = {};
    for (const [kind, anchor] of Object.entries(model.modAnchors)) {
      if (anchor === undefined) continue;
      const projected = model.group.localToWorld(anchor.clone()).project(this.camera);
      points[kind as AttachKind] = {
        x: (projected.x * 0.5 + 0.5) * this.widthPx,
        y: (-projected.y * 0.5 + 0.5) * this.heightPx,
        visible: projected.z >= -1 && projected.z <= 1,
      };
    }
    this.onAnchors(points);
  }
}

export type ModAnchorPoints = Partial<
  Record<AttachKind, { x: number; y: number; visible: boolean }>
>;
